"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import { Menu, X, Car, ChevronDown, LogOut, LayoutDashboard, User, Search, GitCompare } from "lucide-react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { createClient } from "@/lib/supabase/client";
import Button from "@/components/ui/Button";
import LocaleSwitcher from "@/components/layout/LocaleSwitcher";
import ThemeToggle from "@/components/layout/ThemeToggle";
import CatalogSearchModal from "@/components/layout/CatalogSearchModal";

interface UserProfile {
  full_name: string | null;
  avatar_url: string | null;
  role: string;
}

function getDashboardLink(role: string) {
  if (role === "admin" || role === "superadmin") return "/admin";
  if (role === "rentacar") return "/vendor/dashboard";
  return "/dashboard";
}

function getInitials(name: string | null, email: string) {
  if (name) return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  return email[0].toUpperCase();
}

export default function Navbar() {
  const t = useTranslations("Nav");
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const [catalogSearchOpen, setCatalogSearchOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [user, setUser] = useState<{ email: string; id: string } | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const supabase = createClient();

    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      setUser({ email: user.email ?? "", id: user.id });
      supabase
        .from("profiles")
        .select("full_name, avatar_url, role")
        .eq("user_id", user.id)
        .single()
        .then(({ data }) => setProfile(data));
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        setUser(null);
        setProfile(null);
        return;
      }
      setUser({ email: session.user.email ?? "", id: session.user.id });
      supabase
        .from("profiles")
        .select("full_name, avatar_url, role")
        .eq("user_id", session.user.id)
        .single()
        .then(({ data }) => setProfile(data));
    });

    return () => subscription.unsubscribe();
  }, []);

  // Закрыть dropdown при клике вне
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  async function handleLogout() {
    const supabase = createClient();
    await supabase.auth.signOut();
    router.push("/");
    router.refresh();
  }

  const displayName = profile?.full_name ?? user?.email ?? "";
  const initials = user ? getInitials(profile?.full_name ?? null, user.email) : "";
  const dashboardLink = profile ? getDashboardLink(profile.role) : "/dashboard";

  return (
    <header className="sticky top-0 z-50 border-b border-gray-100 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-950/95 dark:shadow-gray-950/50 dark:backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex h-16 items-center gap-1.5 sm:gap-2 md:gap-2 lg:gap-3">

          {/* Logo */}
          <Link href="/" className="relative z-[2] flex shrink-0 items-center gap-2 font-bold text-xl text-gray-900 dark:text-gray-100">
            <span className="bg-[#E63946] text-white p-1.5 rounded-lg">
              <Car size={18} />
            </span>
            BeepBeep<span className="text-[#E63946]">.tj</span>
          </Link>

          {/* Desktop nav — по центру шапки */}
          <nav
            className="absolute left-1/2 top-1/2 z-[1] hidden max-w-[min(100%-10rem,calc(100vw-18rem))] -translate-x-1/2 -translate-y-1/2 items-center gap-2 text-sm font-medium text-gray-600 md:flex lg:gap-4 xl:gap-5 dark:text-gray-400"
            aria-label={t("mainNavigation")}
          >
            <Link href="/cars" className="whitespace-nowrap transition-colors hover:text-gray-900 dark:hover:text-gray-100">{t("catalog")}</Link>
            <Link href="/cars/compare" className="flex items-center gap-1 whitespace-nowrap transition-colors hover:text-gray-900 dark:hover:text-gray-100">
              {t("compare")}
            </Link>
            <Link href="/cars/brands" className="whitespace-nowrap transition-colors hover:text-gray-900 dark:hover:text-gray-100">{t("brands")}</Link>
            <Link href="/vendors" className="flex items-center gap-1 whitespace-nowrap transition-colors hover:text-gray-900 dark:hover:text-gray-100">
              {t("vendors")}
            </Link>
            <Link href="/about" className="whitespace-nowrap transition-colors hover:text-gray-900 dark:hover:text-gray-100">{t("about")}</Link>
            <Link href="/contact" className="whitespace-nowrap transition-colors hover:text-gray-900 dark:hover:text-gray-100">{t("contact")}</Link>
          </nav>

          {/* Desktop actions */}
          <div className="relative z-[2] ml-auto hidden shrink-0 items-center gap-1.5 md:flex">
            <button
              type="button"
              onClick={() => setCatalogSearchOpen(true)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
              aria-label={t("search")}
              title={t("search")}
            >
              <Search size={20} strokeWidth={2} aria-hidden />
            </button>
            <ThemeToggle />
            <LocaleSwitcher />
            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-1.5 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                  aria-label={t("profile")}
                >
                  {/* Avatar */}
                  {profile?.avatar_url ? (
                    <Image
                      src={profile.avatar_url}
                      alt={displayName}
                      width={28}
                      height={28}
                      className="h-7 w-7 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#E63946] text-white text-xs font-bold flex items-center justify-center">
                      {initials}
                    </div>
                  )}
                  <span className="max-w-[120px] truncate text-sm font-medium text-gray-800 dark:text-gray-200">
                    {profile?.full_name ?? user.email.split("@")[0]}
                  </span>
                  <ChevronDown size={14} className={`text-gray-400 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {/* Dropdown */}
                {dropdownOpen && (
                  <div className="absolute right-0 z-50 mt-2 w-52 rounded-xl border border-gray-100 bg-white py-1 shadow-lg dark:border-gray-800 dark:bg-gray-900">
                    <div className="border-b border-gray-50 px-4 py-2.5 dark:border-gray-800">
                      <p className="truncate text-xs font-semibold text-gray-900 dark:text-gray-100">{displayName}</p>
                      <p className="truncate text-xs text-gray-400">{user.email}</p>
                    </div>
                    <Link
                      href={dashboardLink}
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                    >
                      <LayoutDashboard size={15} className="text-gray-400" />
                      {t("dashboard")}
                    </Link>
                    <Link
                      href="/dashboard/profile"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                    >
                      <User size={15} className="text-gray-400" />
                      {t("profile")}
                    </Link>
                    <div className="mt-1 border-t border-gray-50 dark:border-gray-800">
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-950/40"
                      >
                        <LogOut size={15} />
                        {t("logout")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link href="/auth/login">
                  <Button variant="outline" size="sm">{t("login")}</Button>
                </Link>
                <Link href="/auth/register">
                  <Button size="sm">{t("register")}</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile search + menu */}
          <div className="relative z-[2] ml-auto flex shrink-0 items-center gap-0.5 md:hidden">
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
              aria-label={t("search")}
              title={t("search")}
              onClick={() => setCatalogSearchOpen(true)}
            >
              <Search size={20} strokeWidth={2} aria-hidden />
            </button>
            <button
              className="rounded-lg p-2.5 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={t("toggleMenu")}
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="relative z-[60] flex flex-col gap-3 border-t border-gray-100 bg-white px-4 py-4 dark:border-gray-800 dark:bg-gray-950 md:hidden">
          <div className="flex items-center gap-2 pb-2">
            <ThemeToggle />
            <LocaleSwitcher />
          </div>
          <Link href="/cars" className="py-2 text-sm font-medium text-gray-700 dark:text-gray-300" onClick={() => setMenuOpen(false)}>{t("catalog")}</Link>
          <Link href="/cars/compare" className="flex items-center gap-2 py-2 text-sm font-medium text-gray-700 dark:text-gray-300" onClick={() => setMenuOpen(false)}>
            <GitCompare size={16} aria-hidden />
            {t("compare")}
          </Link>
          <Link href="/cars/brands" className="py-2 text-sm font-medium text-gray-700 dark:text-gray-300" onClick={() => setMenuOpen(false)}>{t("brands")}</Link>
          <Link href="/vendors" className="py-2 text-sm font-medium text-gray-700 dark:text-gray-300" onClick={() => setMenuOpen(false)}>{t("vendors")}</Link>
          <Link href="/about" className="py-2 text-sm font-medium text-gray-700 dark:text-gray-300" onClick={() => setMenuOpen(false)}>{t("about")}</Link>
          <Link href="/contact" className="py-2 text-sm font-medium text-gray-700 dark:text-gray-300" onClick={() => setMenuOpen(false)}>{t("contact")}</Link>

          <div className="border-t border-gray-100 pt-3 dark:border-gray-800">
            {user ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3 py-2">
                  <div className="w-8 h-8 rounded-full bg-[#E63946] text-white text-xs font-bold flex items-center justify-center">
                    {initials}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{profile?.full_name ?? user.email.split("@")[0]}</p>
                    <p className="text-xs text-gray-400">{user.email}</p>
                  </div>
                </div>
                <Link href={dashboardLink} onClick={() => setMenuOpen(false)}>
                  <Button variant="outline" size="sm" className="w-full">{t("dashboard")}</Button>
                </Link>
                <Button variant="ghost" size="sm" className="w-full text-red-500" onClick={handleLogout}>
                  {t("logout")}
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Link href="/auth/login" className="flex-1" onClick={() => setMenuOpen(false)}>
                  <Button variant="outline" size="sm" className="w-full">{t("login")}</Button>
                </Link>
                <Link href="/auth/register" className="flex-1" onClick={() => setMenuOpen(false)}>
                  <Button size="sm" className="w-full">{t("register")}</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      <CatalogSearchModal open={catalogSearchOpen} onClose={() => setCatalogSearchOpen(false)} />
    </header>
  );
}
